import type { ApiOpts, IEventChannel } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";

export default class LoggingModule {

    eventChannel: IEventChannel;
    readonly rqBroker: AjaxRequestBroker;
    readonly resourceName = "logging";

    errorCountSincePageLoad = 0;
    MAX_ERRORS_PER_PAGE_LOAD = 10;

    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;

        this.eventChannel.addListener("onDestroy", () => {
            this.eventChannel = null;
        })
    }

    /**
     * @function es.module.loggingModule.clientsideErr
     * Log a clientside error to the server. Note: a max of 10 errors can be logged per page load, to avoid potentially bombarding the server with errors.
     * @param msg note: msg is string, use JSON.stringify() on the msg param beforehand if it's a JSON object
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    clientsideErr(msg: string, opts?: ApiOpts): JQueryPromise<any> {
        if(this.errorCountSincePageLoad > this.MAX_ERRORS_PER_PAGE_LOAD){
            console.error("skip submitting error to server. max (" + this.MAX_ERRORS_PER_PAGE_LOAD + ") already submitted since page load.", msg);
            return;
        }

        this.errorCountSincePageLoad++;

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + "/clientside_err",
            data: msg,
            dataType: "text",
            contentType: "text/plain",
            opts: { doNotJsonify: true }
        }).fail((xhr, status, errorThrown) => {
            console.error("errorReport : error logging clientsideErr to eS", xhr, status, errorThrown);
        });
    }

    /**
     * @function es.module.loggingModule.errorReport
     * User submits error report to the server, usually after experiencing a server-side error.
     * @param msg note: msg is string, use JSON.stringify() on the msg param beforehand if it's a JSON object
     * @param opts
     * @return a jquery deferred object resolved when the request has completed
     */
    errorReport(msg: string, opts?: ApiOpts): JQueryPromise<any> {
        console.error("errorReport : submitting to server..." , msg);

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + "/error_report",
            data: msg,
            dataType: "text",
            contentType: "text/plain",
            opts: { doNotJsonify: true }
        }).fail((xhr, status, errorThrown) => {
            console.error("errorReport : error logging error report to eS", xhr, status, errorThrown);
        });
    }

    // deliberate error on server, useful to test err handling and reporting
    deliberateServerError(): JQueryPromise<any> {
        return this.rqBroker.doGet({
            url: es.api.getURL(this.resourceName) + "/deliberate_error"
        });
    }

    // deliberate error on client, useful to test er handling and reporting
    deliberateClientError(): void {
        const a = null;
        a.invalidFunctionCall();
    }

    deliberateSalesforceTimeoutError() {
        if(es.isEmbeddedInSFDC) {
            es.SFDC.client.signedRequest.client.oauthToken = "foo";
            es.SFDC.client.getCampaigns();
        } else {
            es.log.warn("es.isEmbeddedInSFDC", es.isEmbeddedInSFDC)
        }
    }
}